import { useMemo } from 'react';
import { t } from '@app/i18n';
import { useLocale } from '@app/i18n/useLocale';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ROLES, PASSWORD_MIN, type UserFormValues } from './UserModel';

/**
 * Validação do formulário de usuário (T22, FR-106).
 *
 * A senha só é exigida no modo **digitar** — no modo **gerar** quem a produz é o
 * servidor, e ela volta uma única vez. O mínimo de 8 vale para *definir* senha,
 * nunca para entrar (FR-003).
 */
export function userSchema(): yup.ObjectSchema<UserFormValues> {
  return yup.object({
    name: yup.string().trim().required(t('users.form.validation.nameRequired')),
    email: yup
      .string()
      .trim()
      .required(t('users.form.validation.emailRequired'))
      .email(t('users.form.validation.emailInvalid')),
    role: yup.string().oneOf(ROLES).required(t('users.form.validation.roleRequired')) as yup.Schema<
      UserFormValues['role']
    >,
    modoSenha: yup.string().oneOf(['gerar', 'digitar']).required() as yup.Schema<
      UserFormValues['modoSenha']
    >,
    // No modo **gerar** o campo nem aparece: string vazia é o valor legítimo, e
    // quem produz a senha é o servidor.
    password: yup
      .string()
      .defined()
      .when('modoSenha', {
        is: 'digitar',
        then: (schema) =>
          schema
            .required(t('users.form.validation.passwordRequired'))
            .min(PASSWORD_MIN, t('users.form.validation.passwordMin', { min: PASSWORD_MIN })),
      }) as yup.Schema<string>,
    autoApproveRequests: yup.boolean().defined(),
  });
}

export function useUserValidation() {
  // O `locale` não aparece no corpo e ainda assim é dependência: as mensagens
  // são montadas na construção do schema, então trocar de idioma com o drawer
  // aberto deixaria a validação no idioma anterior até o próximo `mount`.
  const locale = useLocale();
  // O lint não enxerga a leitura do idioma feita pelo `t()` lá dentro.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schema = useMemo(() => userSchema(), [locale]);
  return {
    resolver: yupResolver(schema),
    mode: 'onChange' as const,
  };
}
